// src/app/services/rental.service.ts
import { Injectable } from '@angular/core';
import {
  Firestore,
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  collectionData,
  docData,
  where,
  query,
  serverTimestamp,
  arrayUnion,
  arrayRemove
} from '@angular/fire/firestore';
import { Rental } from '../models/rental.model';
import { combineLatest, map, Observable, of, switchMap } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class RentalService {
  constructor(private firestore: Firestore) {}



  addRental(rental: Omit<Rental, 'id'>) {
    const rentalsRef = collection(this.firestore, 'rentals');
    return addDoc(rentalsRef, {
      ...rental,
      createdAt: serverTimestamp()
    });
  }
  async assignRentalToUser(rentalId: string, uid: string) {
    const rentalRef = doc(this.firestore, `rentals/${rentalId}`);
    await updateDoc(rentalRef, {
      tenantIds: arrayUnion(uid) // <-- allows multiple users per rental
    });
  }
  async removeUserFromRental(rentalId: string, uid: string) {
    const rentalRef = doc(this.firestore, `rentals/${rentalId}`);
    await updateDoc(rentalRef, {
      tenantIds: arrayRemove(uid)
    });
  }
  /** Rentals enriched with landlord name */
    getRentalsWithLandlordName(): Observable<any[]> {

      return collectionData(collection(this.firestore, 'rentals'), { idField: 'id' }).pipe(
        switchMap(rentals => {
          if (!rentals.length) return [ [] ]; // empty array

          // Map each rental to an observable of its landlord
          const enriched$ = rentals.map(rental => {
            if (!rental.landlordId) return { ...rental, landlordName: 'N/A' };

            const landlordDocRef = doc(this.firestore, `landlords/${rental.landlordId}`);
            return docData(landlordDocRef).pipe(
              map(landlord => ({ ...rental, landlordName: landlord?.name || 'N/A' }))
            );
          });

          return combineLatest(enriched$);
        })
      );
    }

  /** LIST */
  getRentals(): Observable<Rental[]> {
    return collectionData(
      collection(this.firestore, 'rentals'),
      { idField: 'id' } // ✅ CRITICAL
    ) as Observable<Rental[]>;
  }
  async openApplicationRound(rentalId: string): Promise<string> {
  const roundRef = await addDoc(
    collection(this.firestore, 'applicationRounds'),
    {
      rentalId,
      status: 'open',
      createdAt: serverTimestamp()
    }
  );

  await updateDoc(doc(this.firestore, 'rentals', rentalId), {
    activeRoundId: roundRef.id,
    status: 'available'
  });

  return roundRef.id;
}

async closeApplicationRound(roundId: string) {
  const roundSnap = await getDoc(doc(this.firestore, 'applicationRounds', roundId));
  const rentalId = roundSnap.data()?.rentalId;

  await updateDoc(doc(this.firestore, 'applicationRounds', roundId), {
    status: 'closed',
    closedAt: serverTimestamp()
  });

  if (rentalId) {
    await updateDoc(doc(this.firestore, 'rentals', rentalId), {
      activeRoundId: null
    });
  }
}

  getRentalsByIds(ids: string[]) {
    if (!ids.length) return of([]);

    const chunks = [];
    while (ids.length) chunks.push(ids.splice(0, 10));

    return combineLatest(
      chunks.map(chunk =>
        collectionData(
          query(
            collection(this.firestore, 'rentals'),
            where('__name__', 'in', chunk)
          ),
          { idField: 'id' }
        )
      )
    ).pipe(map(results => results));
}
getRentalsWithLandlord(landlordIds: string[]): Observable<Rental[]> {
    const rentalsRef = collection(this.firestore, 'rentals');

    const q = landlordIds.length === 1
      ? query(rentalsRef, where('landlordId', '==', landlordIds[0]))
      : query(rentalsRef, where('landlordId', 'in', landlordIds));

    return collectionData(q, { idField: 'id' }).pipe(
      switchMap((rentals: any[]) => {
        if (!rentals.length) return of([]);

        const landlordIds = [...new Set(rentals.map(r => r.landlordId))];

        const landlordQueries = landlordIds.map(id =>
          docData(doc(this.firestore, `landlords/${id}`)).pipe(
            map(landlord => ({ id, landlord }))
          )
        );

        return combineLatest(landlordQueries).pipe(
          map(landlords =>
            rentals.map(rental => {
              const landlord = landlords.find(l => l.id === rental.landlordId);
              return {
                id: rental.id,
                title: rental.title,
                address: rental.address,
                price: rental.price,
                landlordId: rental.landlordId,
                landlordName: landlord?.landlord?.name ?? 'Unknown'
              };
            })
          )
        );
      })
    );
  }
  getVacantRentals(): Observable<Rental[]> {
    const rentalsRef = collection(this.firestore, 'rentals');
    const q = query(rentalsRef, where('status', '==', 'vacant'));

    return collectionData(q, { idField: 'id' }) as Observable<Rental[]>;
  }
  getAvailableRentals(): Observable<Rental[]> {
    const rentalsRef = collection(this.firestore, 'rentals');
    const q = query(rentalsRef, where('status', '==', 'available'));

    return collectionData(q, { idField: 'id' }) as Observable<Rental[]>;
  }

   /** SINGLE */
  getRentalById(id: string): Observable<Rental | null> {
    return docData(
      doc(this.firestore, 'rentals', id),
      { idField: 'id' }
    ) as Observable<Rental | null>;
  }

  /** UPDATE */
  updateRental(id: string, data: Partial<Rental>) {
    return updateDoc(doc(this.firestore, 'rentals', id), {
      ...data,
      updatedAt: new Date()
    });
  }

  /** DELETE */
  deleteRental(id: string) {
    return deleteDoc(doc(this.firestore, 'rentals', id));
  }
}
